import { injectAesthetic } from '../library/ui/shared-styles.ts';

export function getPluginEditorStyles(seed: number, colWeights: number[]) {
    return `
        ${injectAesthetic(seed)}
        <style>
            :host {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: #0d1117;
                color: white;
                z-index: 2147483647; /* Max Z-index to cover everything including menu */
                display: flex;
                flex-direction: column;
                font-family: var(--font-main, sans-serif);

                /* Design Tokens - Standardized Drafting Table Palette */
                --bg-card: #161b22;
                --bg-main: #0d1117;
                --text-main: #c9d1d9;
                --text-dim: #8b949e;
                --border: #30363d;
                --border-bright: #444c56;
                --radius-md: 8px;
                --accent-soft: rgba(245, 158, 11, 0.1);
                --font-mono: 'JetBrains Mono', 'Fira Code', monospace;
                --shadow-premium: 0 8px 24px rgba(0, 0, 0, 0.5);
            }

            * {
                box-sizing: border-box;
            }

            select option {
                background: #161b22 !important;
                color: #c9d1d9 !important;
            }

            /* Global Scrollbar Customization */
            *::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }

            *::-webkit-scrollbar-track {
                background: rgba(0, 0, 0, 0.1);
            }

            *::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.1);
                border-radius: 4px;
            }

            *::-webkit-scrollbar-thumb:hover {
                background: var(--accent);
            }

            .hidden {
                display: none !important;
            }

            .editor-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0.5rem 1rem;
                background: #0d1117;
                border-bottom: 1px solid var(--border-bright);
                height: 48px;
                flex-shrink: 0;
            }

            .preview-header {
                padding: 8px 12px;
                background: rgba(0, 0, 0, 0.2);
                border-bottom: 1px solid var(--border-sketch);
                flex-shrink: 0;
                max-height: 180px;
                overflow-y: auto;
            }

            .header-title {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }

            .header-title h2 {
                margin: 0;
                font-family: var(--font-sketch);
                color: var(--accent);
                font-size: 1.2rem;
                letter-spacing: 0.01em;
            }

            .header-actions {
                display: flex;
                align-items: center;
                gap: 0.75rem;
            }

            .editor-body {
                flex: 1;
                display: flex;
                flex-direction: column;
                padding: 0.5rem;
                gap: 0.5rem;
                overflow: hidden;
                background: var(--bg-main);
            }

            .config-bar {
                display: flex;
                align-items: center;
                gap: 1.5rem;
                padding: 0.5rem 1rem;
                background: var(--bg-main);
                border-bottom: 1px solid var(--border);
                flex-shrink: 0;
            }

            .config-bar > * {
                margin-bottom: 0 !important;
                margin-top: 0 !important;
            }

            .config-bar-container {
                display: flex;
                flex-direction: column;
                background: var(--bg-main);
                flex-shrink: 0;
                border-radius: var(--radius-md);
                border: 1px solid var(--border);
                overflow: hidden;
            }

            .mock-drawer {
                display: flex;
                flex-wrap: wrap;
                gap: 1.5rem;
                padding: 1.25rem;
                background: rgba(255, 255, 255, 0.02);
                border-bottom: 1px solid var(--border);
                animation: slideDown 0.3s ease-out;
                overflow: hidden;
            }

            @keyframes slideDown {
                from { transform: translateY(-10px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
            }

            .form-input-compact {
                background: #0d1117;
                border: 1px solid var(--border-bright);
                color: #c9d1d9;
                border-radius: 4px;
                padding: 0 10px;
                height: 32px;
                font-size: 0.85rem;
                width: 100%;
                outline: none;
                transition: all 0.2s;
                font-family: inherit;
                cursor: pointer;
            }

            .form-input-compact:focus {
                border-color: var(--accent);
                background: #161b22;
            }

            select.form-input-compact option {
                background: #161b22 !important;
                color: #c9d1d9 !important;
            }

            .control-group {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .control-group label {
                font-family: var(--font-sketch);
                letter-spacing: 0.02em;
            }

            .config-bar form-input, 
            .config-bar form-select,
            .config-bar form-toggle {
                margin-bottom: 0 !important;
            }

            .config-toggle-row {
                display: flex;
                align-items: center;
                height: 32px;
            }

            /* Fix for middle column form overflow */
            .config-form-container form-input,
            .config-form-container form-select,
            .config-form-container form-textarea {
                width: 100% !important;
                max-width: 100% !important;
            }

            .mock-drawer app-button {
                height: 32px;
                display: flex;
                align-items: center;
            }

            .config-toggle-wrapper {
                align-self: flex-end;
                display: flex;
                align-items: center;
                height: 34px;
            }

            .mapping-grid {
                flex: 1;
                display: grid;
                grid-template-columns: ${colWeights[0]}fr 4px ${colWeights[1]}fr 4px ${colWeights[2]}fr;
                gap: 0;
                min-height: 0;
                width: 100%;
                box-sizing: border-box;
            }

            .mapping-col {
                display: flex;
                flex-direction: column;
                min-width: 0;
                min-height: 0;
                padding: 0;
                background: rgba(0,0,0,0.05);
                border-right: 1px solid var(--border-bright);
                transition: all 0.3s ease;
            }

            .mapping-col[data-index="0"] {
                gap: 0;
                padding: 0;
            }

            .col-row-top {
                flex: 1.5;
                display: flex;
                flex-direction: column;
                min-height: 0;
                padding: 0.5rem;
                gap: 0.5rem;
            }

            .col-row-bottom {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-height: 0;
                padding: 0.5rem;
                gap: 0.5rem;
                border-top: 1px solid var(--border-bright);
                background: rgba(0, 0, 0, 0.05);
            }

            .resizer {
                width: 4px;
                cursor: col-resize;
                background: transparent;
                transition: background 0.2s;
                z-index: 10;
            }

            .resizer:hover, .resizer.active {
                background: var(--accent);
                box-shadow: 0 0 8px var(--accent);
            }



            .col-content-padded {
                padding: 0.5rem;
                display: flex;
                flex-direction: column;
                min-height: 0;
            }

            .section-tag {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 0.65rem;
                font-weight: 700;
                text-transform: uppercase;
                margin-top: 0;
                align-self: flex-start;
            }

            .tag-request { color: #3b82f6; background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.2); }
            .tag-vars { color: #8b5cf6; background: rgba(139, 92, 246, 0.1); border: 1px solid rgba(139, 92, 246, 0.2); }
            .tag-logic { color: #f59e0b; background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.2); }
            .tag-config { color: #10b981; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.2); }
            .tag-preview { color: #ec4899; background: rgba(236, 72, 153, 0.1); border: 1px solid rgba(236, 72, 153, 0.2); }
            .tag-readonly { color: #64748b; background: rgba(100, 116, 139, 0.1); border: 1px solid rgba(100, 116, 139, 0.2); }

            .editor-container {
                flex: 1;
                display: flex;
                position: relative;
                min-height: 0;
                border: 1px solid #30363d;
                border-radius: 4px;
                overflow: hidden;
                background: #0d1117;
            }

            .editor-content {
                flex: 1;
                min-height: 0;
                padding: 1rem;
                font-family: var(--font-mono, monospace);
                font-size: 0.9rem;
                line-height: 1.5;
                color: #e6edf3;
                outline: none;
                overflow-y: auto;
                white-space: pre;
                tab-size: 4;
            }

            .editor-content.editable:focus {
                background: rgba(255, 255, 255, 0.02);
            }

            .line-numbers {
                width: 45px;
                padding: 1rem 0.5rem;
                text-align: right;
                color: #484f58;
                font-family: var(--font-mono, monospace);
                font-size: 0.8rem;
                background: #0d1117;
                border-right: 1px solid #30363d;
                user-select: none;
                overflow: hidden;
            }

            .config-form-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                padding: 1.5rem;
                overflow-y: auto;
                background: #0d1117;
                border: 1px solid #30363d;
                border-radius: 4px;
            }

            .form-config-box {
                display: flex;
                flex-direction: column;
                gap: 1.5rem;
                width: 100%;
                max-width: 600px;
            }

            .form-label {
                font-size: 0.85rem;
                color: #8b949e;
                font-weight: 600;
                margin-bottom: -0.5rem;
            }

            .form-textarea {
                background: #0d1117;
                border: 1px solid #30363d;
                border-radius: 6px;
                color: #c9d1d9;
                padding: 0.75rem;
                font-family: var(--font-mono);
                resize: vertical;
                min-height: 100px;
                width: 100%;
            }

            .checkbox-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                gap: 1rem;
                margin-top: 1rem;
                background: #0d1117;
                padding: 1rem;
                border-radius: 6px;
                border: 1px solid #30363d;
            }

            .checkbox-label {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                font-size: 0.85rem;
                color: #c9d1d9;
                cursor: pointer;
            }

            .help-text {
                font-size: 0.75rem;
                color: #8b949e;
                margin-top: -0.25rem;
                margin-bottom: 0.5rem;
                font-style: italic;
            }

            .form-field-group {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                margin-bottom: 1.5rem;
            }

            .form-field-group:last-child {
                margin-bottom: 0;
            }

            .col-body {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-height: 0;
                gap: 0.5rem;
            }





            .sim-result-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-height: 0;
                background: #010409;
                border: 1px solid #30363d;
                border-radius: 6px;
            }

            .result-tabs {
                display: flex;
                background: #161b22;
                border-bottom: 1px solid #30363d;
                padding: 0 0.5rem;
            }

            .sim-toggle-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                height: 100%;
                padding-bottom: 8px;
            }

            .result-tab {
                padding: 0.5rem 1rem;
                font-size: 0.8rem;
                cursor: pointer;
                border-bottom: 2px solid transparent;
                color: #8b949e;
            }

            .result-tab.active {
                color: var(--accent);
                border-bottom-color: var(--accent);
            }

            .result-content {
                flex: 1;
                overflow: auto;
                padding: 1rem;
                font-family: var(--font-mono);
                font-size: 0.85rem;
                white-space: pre-wrap;
            }

            .syntax-error {
                color: #ff7b72;
                background: rgba(248, 81, 73, 0.1);
                padding: 0.5rem;
                border-radius: 4px;
                margin-top: 0.5rem;
                font-size: 0.8rem;
            }

            .loading-overlay {
                position: absolute;
                inset: 0;
                background: rgba(13, 17, 23, 0.7);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 100;
                backdrop-filter: blur(2px);
            }

            .two-col-layout {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1.5rem;
                height: 100%;
            }

            .left-panel, .right-panel {
                display: flex;
                flex-direction: column;
                min-height: 0;
                gap: 1rem;
            }

            .simple-config-card {
                background: #161b22;
                border: 1px solid #30363d;
                border-radius: 8px;
                padding: 1.5rem;
            }

            .domain-badge {
                font-size: 0.7rem;
                background: rgba(59, 130, 246, 0.1);
                color: #3b82f6;
                padding: 2px 6px;
                border-radius: 4px;
                border: 1px solid rgba(59, 130, 246, 0.2);
            }

            .method-badge {
                font-size: 0.7rem;
                font-weight: bold;
                padding: 2px 6px;
                border-radius: 4px;
                margin-right: 4px;
            }

            .method-get { background: #2ea04333; color: #3fb950; }
            .method-post { background: #a371f733; color: #bc8cff; }
            .method-put { background: #d2992233; color: #d29922; }
            .method-delete { background: #f8514933; color: #f85149; }

            .sim-row {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                min-width: 0;
            }

            .field-row {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                flex: 1;
                min-width: 0;
            }

            .field-row label {
                font-size: 0.65rem;
                color: var(--text-dim);
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
            }

            .sim-controls-v2 select {
                background: #0d1117;
                border: 1px solid var(--border);
                border-radius: 4px;
                color: var(--text-main);
                padding: 4px 8px;
                font-size: 0.8rem;
                width: 100%;
                outline: none;
                transition: border-color 0.2s;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }
            .sim-result-meta {
                display: flex;
                align-items: center;
                gap: 1rem;
                padding: 0.5rem 1rem;
                background: rgba(255, 255, 255, 0.02);
                border-bottom: 1px solid var(--border);
                font-size: 0.75rem;
                color: var(--text-dim);
            }

            .status-badge {
                font-size: 0.7rem;
                font-weight: bold;
                padding: 1px 6px;
                border-radius: 4px;
            }

            .status-2xx { background: #2ea043; color: #fff; }
            .status-4xx { background: #f85149; color: #fff; }
            .status-5xx { background: #f85149; color: #fff; }

            .form-label {
                font-size: 0.7rem;
                font-weight: 700;
                color: var(--text-dim);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 0.25rem;
                display: block;
            }

            .form-textarea {
                width: 100%;
                max-width: 100%;
                background: #0d1117;
                border: 1px solid var(--border);
                border-radius: 6px;
                color: var(--text-main);
                padding: 0.75rem;
                font-family: var(--font-mono);
                font-size: 0.85rem;
                resize: vertical;
                outline: none;
                transition: border-color 0.2s;
            }

            .form-textarea:focus {
                border-color: var(--accent, #f59e0b);
                box-shadow: 0 0 0 2px var(--accent-soft);
            }

            .checkbox-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                gap: 0.75rem;
                padding: 0.5rem;
                background: rgba(255, 255, 255, 0.02);
                border-radius: 6px;
                border: 1px solid var(--border);
            }

            .sim-controls-v2 select:focus {
                border-color: var(--accent);
            }





            .context-list {
                flex: 1;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                padding-right: 0.25rem;
            }

            .context-item {
                display: flex;
                justify-content: space-between;
                padding: 0.4rem 0.6rem;
                background: rgba(255, 255, 255, 0.03);
                border-radius: 3px;
                font-size: 0.8rem;
                cursor: pointer;
                transition: all 0.2s ease;
                border: 1px solid transparent;
            }

            .context-item:hover {
                background: rgba(255, 255, 255, 0.08);
                border-color: var(--border-bright);
                transform: translateX(2px);
            }

            .context-key {
                color: var(--brand-secondary);
                font-family: var(--font-mono);
                font-weight: 500;
            }

            .context-val {
                color: var(--text-dim);
                font-family: var(--font-mono);
                max-width: 150px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            /* Section tags color variants */
            .tag-logic { background: rgba(56, 139, 253, 0.15); color: #58a6ff; border: 1px solid rgba(56, 139, 253, 0.3); }
            .tag-config { background: rgba(46, 160, 67, 0.15); color: #3fb950; border: 1px solid rgba(46, 160, 67, 0.3); }
            .tag-readonly { background: rgba(255, 255, 255, 0.05); color: var(--text-dim); border: 1px solid var(--border); }
            .tag-request { background: rgba(163, 113, 247, 0.15); color: #d397ff; border: 1px solid rgba(163, 113, 247, 0.3); }
            .tag-vars { background: rgba(210, 153, 34, 0.15); color: #e3b341; border: 1px solid rgba(210, 153, 34, 0.3); }

            /* Hidden column utility */
            .hidden {
                display: none !important;
            }

            .loader-sm {
                width: 14px;
                height: 14px;
                border: 2px solid rgba(255, 255, 255, 0.2);
                border-top-color: #fff;
                border-radius: 50%;
                animation: spin 0.6s linear infinite;
                display: inline-block;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }

            .simulation-controls {
                display: flex;
                gap: 1rem;
                align-items: flex-end;
                margin-bottom: 1.5rem;
                padding: 0 1rem;
                min-width: 0;
            }

            .simulation-controls select {
                max-width: 400px;
                min-width: 0;
                text-overflow: ellipsis;
            }

            .preview-tabs {
                display: flex;
                gap: 0.5rem;
                margin-bottom: 1rem;
                border-bottom: 1px solid #30363d;
                padding-bottom: 0.5rem;
            }

            .preview-tab {
                padding: 0.4rem 0.8rem;
                font-size: 0.8rem;
                cursor: pointer;
                border-radius: 4px;
                color: #8b949e;
                background: transparent;
                border: 1px solid transparent;
            }

            .preview-tab:hover {
                color: #c9d1d9;
                background: #30363d;
            }

            .preview-tab.active {
                color: var(--accent);
                background: rgba(245, 158, 11, 0.1);
                border-color: rgba(245, 158, 11, 0.2);
            }

            .context-list {
                flex: 1;
                overflow-y: auto;
                background: #0d1117;
                border: 1px solid #30363d;
                border-radius: 4px;
                padding: 0.25rem;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .context-item {
                display: flex;
                flex-direction: column;
                padding: 0.4rem 0.6rem;
                border-radius: 3px;
                cursor: pointer;
                transition: background 0.2s;
            }

            .context-item:hover {
                background: rgba(255, 255, 255, 0.05);
            }

            .context-key {
                font-family: var(--font-mono);
                font-size: 0.75rem;
                color: var(--accent);
                font-weight: 600;
            }

            .context-val {
                font-size: 0.65rem;
                color: var(--text-dim);
                margin-top: 2px;
            }

            .source-selector {
                display: flex;
                flex-direction: column;
                gap: 0.4rem;
                padding: 0.6rem;
                background: rgba(0, 0, 0, 0.2);
                border: 1px solid var(--border);
                border-radius: 6px;
                margin-bottom: 0.6rem;
                flex-shrink: 0;
            }

            .source-selector .control-group {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .source-selector label {
                font-size: 0.65rem;
                color: var(--text-dim);
                font-weight: 700;
                text-transform: uppercase;
            }

            .source-selector select {
                background: #0d1117;
                border: 1px solid var(--border);
                border-radius: 4px;
                color: var(--text-main);
                padding: 4px 8px;
                font-size: 0.8rem;
                width: 100%;
                outline: none;
            }
        </style>
    `;
}
